import React, { useMemo } from "react";

/**
 * DefaultLetterHeadV2
 * A clean, print-friendly letterhead:
 * - Valid HTML (no deprecated attributes, no random floats)
 * - Consistent mm-based sizing for predictable print
 * - Graceful wrapping for long fields
 * - Optional motto line
 * - Strong bottom divider
 */
const mm = (n) => `${n}mm`;

const cellBase = {
  padding: "0",
  verticalAlign: "top",
  fontFamily: "Arial, Helvetica, sans-serif",
  color: "#000",
  lineHeight: 1.35,
};

const textLine = {
  display: "block",
  width: "100%",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
};

const DefaultLetterHeadV2 = ({ company = {}, title }) => {
  const {
    badge,
    client_name,
    motto,
    address,
    telephone,
    email_address,
    website,
  } = company || {};

  // Compact contacts row (omits empty parts and spaces nicely)
  const Contacts = useMemo(() => {
    const parts = [];
    if (telephone) parts.push(<span key="tel">Tel: +{telephone}</span>);
    if (email_address) parts.push(<span key="mail">Email: {email_address}</span>);
    if (website) parts.push(<span key="web">Web: {website}</span>);
    if (!parts.length) return null;

    return (
      <div style={{ ...textLine, marginTop: "1.2mm", fontSize: "3.2mm" }}>
        {parts.map((el, i) => (
          <span key={i}>
            {i > 0 ? <span>&nbsp;&nbsp;•&nbsp;&nbsp;</span> : null}
            {el}
          </span>
        ))}
      </div>
    );
  }, [telephone, email_address, website]);

  return (
    <div style={{ width: "100%" }}>
      <table
        width="100%"
        style={{ borderCollapse: "collapse", tableLayout: "fixed" }}
        aria-label="Letterhead"
        className="no-table"
      >
        <tbody>
          <tr>
            {/* Left: Logo */}
            <td style={{ ...cellBase, width: mm(30), paddingRight: mm(4) }}>
              {badge ? (
                <img
                  src={badge}
                  alt={`${client_name || "Company"} logo`}
                  style={{
                    display: "block",
                    width: "100%",
                    maxWidth: mm(30),
                    maxHeight: mm(30),
                    objectFit: "contain",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: mm(30),
                    height: mm(30),
                    border: "1px solid #ddd",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "3mm",
                    color: "#666",
                  }}
                >
                  No Logo
                </div>
              )}
            </td>

            {/* Middle: Identity */}
            <td style={{ ...cellBase, textAlign: "center", padding: 0 }}>
              <div
                style={{
                  ...textLine,
                  fontWeight: 700,
                  fontSize: "6mm",
                  letterSpacing: ".2mm",
                }}
                title={client_name}
              >
                {client_name || "—"}
              </div>

              {motto ? (
                <div
                  style={{
                    ...textLine,
                    fontStyle: "italic",
                    fontSize: "3.5mm",
                    marginTop: "0.7mm",
                  }}
                >
                  {motto}
                </div>
              ) : null}

              {address ? (
                <div style={{ ...textLine, marginTop: "1.2mm", fontSize: "3.2mm" }}>
                  {address}
                </div>
              ) : null}

              {Contacts}
            </td>

            {/* Right spacer (keeps the center block truly centered for print) */}
            <td style={{ ...cellBase, width: mm(30) }} />
          </tr>

          {/* Title row */}
          {title ? (
            <tr>
              <td colSpan={3} style={{ ...cellBase, paddingTop: mm(4) }}>
                <div
                  style={{
                    textAlign: "center",
                    fontWeight: 700,
                    fontSize: "4.2mm",
                  }}
                >
                  <u>{title}</u>
                </div>
              </td>
            </tr>
          ) : null}

          {/* Divider */}
          <tr>
            <td colSpan={3} style={{ ...cellBase, paddingTop: mm(4) }}>
              <div
                aria-hidden="true"
                style={{
                  height: "0",
                  borderBottom: "2px solid #000",
                  boxShadow: "0 1px 0 rgba(0,0,0,.06)",
                }}
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default DefaultLetterHeadV2;
